import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import classNames from 'classnames';

import { ROUTE_HOME, ROUTE_MY_ACCOUNT } from '../../config/routes';
import trashieLogo from '../../images/trashie-logo.webp';
import { isMobile } from '../../../../hooks/useBreakPoint';
import {
  checkRouteWithCloseButton,
  getCurrentRouteParentRoute,
  getNavigationItems,
  getCurrentRouteHeaderBackground,
} from '../../utils/routes';
import ACCOUNT_DRAWER_OPEN_EVENT, { ACCOUNT_DRAWER_STATE_EVENT } from '../../config/events';
import { useFirebaseAuth } from '../../../global/FirebaseProvider/FirebaseProvider';
import HeaderAccount from './HeaderAccount';
import NavigationBar from '../NavigationBar';
import TCNavPill from '../TCNavPill/TCNavPill';
import useRewardsV2Flag from '../../utils/hooks/useRewardsV2Flag';
import useUserBalance from '../../utils/hooks/useUserBalance';

import styles from './Header.module.scss';

const {
  main,
  container,
  logo,
  img,
  ctaContainer,
  cta,
  ctaActive,
  tcPillLeft,
} = styles;

const Header = ({ isBannerVisible }) => {
  const { isSignedIn, userData } = useFirebaseAuth();
  const rewardsV2Enabled = useRewardsV2Flag();
  const balance = useUserBalance(isSignedIn, userData);

  const location = useLocation();
  const history = useHistory();
  const mobile = isMobile();
  const isMobileMyAccount = mobile && location.pathname === ROUTE_MY_ACCOUNT;
  const [isAccountDrawerOpen, setIsAccountDrawerOpen] = useState(false);

  const {
    parentRoute,
    withCloseButton,
    headerBackground,
  } = useMemo(() => ({
    parentRoute: getCurrentRouteParentRoute(),
    withCloseButton: checkRouteWithCloseButton(),
    headerBackground: getCurrentRouteHeaderBackground(),
  }), [location.pathname]);

  const className = classNames(main, {
    shadow: !isBannerVisible && !mobile,
  });

  const goBack = () => {
    if (parentRoute === ROUTE_MY_ACCOUNT) {
      setIsAccountDrawerOpen(true);
      window.dispatchEvent(new Event(ACCOUNT_DRAWER_OPEN_EVENT));
      return;
    }

    if (parentRoute) {
      history.push(parentRoute);
    }
  };

  useEffect(() => {
    const handleDrawerState = (event) => {
      setIsAccountDrawerOpen(Boolean(event?.detail?.isOpen));
    };

    window.addEventListener(ACCOUNT_DRAWER_STATE_EVENT, handleDrawerState);
    return () => window.removeEventListener(ACCOUNT_DRAWER_STATE_EVENT, handleDrawerState);
  }, []);

  if (isMobileMyAccount) {
    return null;
  }

  return (
    <nav className={className} style={{ background: mobile && headerBackground }}>
      <div className={container}>
        {mobile && isSignedIn && userData && (
          <div className={tcPillLeft}>
            <TCNavPill
              balance={balance !== null ? balance : userData.balance?.currentBalance}
            />
          </div>
        )}
        {mobile ? (
          (parentRoute && (
            <NavigationBar
              title=""
              onGoBack={withCloseButton ? undefined : goBack}
              onClose={withCloseButton ? goBack : undefined}
            />
          )) || null
        ) : (
          <Link className={logo} to={ROUTE_HOME}>
            <img className={img} src={trashieLogo} alt="trashie logo" />
          </Link>
        )}
        {isSignedIn && userData && (
          <>
            {!mobile && (
              <div className={ctaContainer}>
                {getNavigationItems(
                  rewardsV2Enabled,
                  userData?.activeSubscription,
                ).map(({ route, label, icon: Icon }) => (
                  <Link
                    className={`${cta} ${
                      (isAccountDrawerOpen
                        ? route === ROUTE_MY_ACCOUNT
                        : location.pathname === route)
                        ? ctaActive
                        : ''
                    }`}
                    to={location.pathname === route ? location : route}
                    key={`headerItem${label}`}
                    onClick={(event) => {
                      if (route !== ROUTE_MY_ACCOUNT) {
                        return;
                      }

                      event.preventDefault();
                      setIsAccountDrawerOpen(true);
                      window.dispatchEvent(new Event(ACCOUNT_DRAWER_OPEN_EVENT));
                    }}
                  >
                    <Icon
                      height={16}
                      width={16}
                      color={(isAccountDrawerOpen
                        ? route === ROUTE_MY_ACCOUNT
                        : route === location.pathname)
                        ? 'var(--trashie-primary-code-blue)'
                        : 'var(--trashie-ui-faded-black-100-b)'}
                    />
                    <span>{label}</span>
                  </Link>
                ))}
              </div>
            )}
            {mobile && parentRoute ? (
              null
            ) : (
              <HeaderAccount />
            )}
          </>
        )}
      </div>
    </nav>
  );
};

export default Header;
